import { React, I18N, variantProvider } from '@/app'
import { Button, View, WorkingAt, TextInput, CategoryExplainer } from '@/components'
import { AppStatus } from '@/redux'
import { APIClient } from '@/services'
import { analytics } from '@/services/analytics/segment'
import { TypeGuards, useValidate, useModal } from '@codeleap/common'
import { useMemo, useState } from 'react'
import { RenderItemProps } from './types'
import { globalState } from '@codeleap/store'
import { DebugItem } from './shared'
import { ScrollWrapper } from './ScrollWrapper'
import { CategoryData } from '@/types'
import { TagType } from '../atoms/TagType'
import { useMutation } from '@tanstack/react-query'
import { Organisation } from '@/features'

const pulseAnswers = globalState({})

const answeredStore = globalState({
  questions: {}
})

export const FeedReviewQuestion = (props: RenderItemProps) => {
  const { item, goToNext } = props
  const [category, setCategory] = useState<CategoryData>(null)
  const categoryModal = useModal()

  const pulseId = useMemo(() => item?.review_question?.id, [item?.review_question])

  const answer = pulseAnswers.use(s => s[pulseId]) ?? { value: undefined }

  const answered = answeredStore.use(s => s[pulseId]) ?? false

  const answerMutation = useMutation({
    mutationFn: APIClient.Pulse.answerQuestion
  })

  const commentValidation = useValidate(answer?.comment ?? '', Organisation.Pulse.validatePulseComment)

  function setAnswer(partial) {
    const current = pulseAnswers.get()?.[pulseId] ?? {}
    pulseAnswers.set({ [pulseId]: { ...current, ...partial } })
  }

  async function handleSubmit() {
    await APIClient.UnknownProfile.ensureProfileCompletion()

    try {
      AppStatus.set('loading')

      await answerMutation.mutateAsync({
        question: pulseId,
        ...answer
      })
      analytics.track('question_from_feed')
      answeredStore.set({
        [pulseId]: true
      })

      goToNext()

      AppStatus.set('done')
    } catch (e) {
      logger.error(e)
      AppStatus.set('idle')
    }
  }

  const onCategoryPress = (currCategory: CategoryData) => {
    setCategory(currCategory)
    categoryModal.toggle(true)
  }

  const submitDisabled =
    !TypeGuards.isNumber(answer?.value) ||
    answerMutation?.isPending ||
    answerMutation?.isSuccess ||
    answered ||
    !commentValidation?.isValid

  return (
    <>
      <DebugItem {...props} />

      <ScrollWrapper>
        <View css={[styles.innerWrapper, { pointerEvents: answerMutation?.isSuccess ? 'none' : 'auto' }]}>
          <TagType icon="note" />

          <WorkingAt />

          <Organisation.Pulse.PulsePage.Header
            question={item?.review_question}
            onCategoryPress={onCategoryPress}
            showWorkingAt={false}
            style={{
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: 0,
              paddingBottom: 0
            }}
          />

          <Organisation.Pulse.PulsePage.Content
            answer={answer}
            setAnswer={setAnswer}
            question={item?.review_question}
            grid
            commentInput={
              <TextInput
                debugName="feedback on pulse questions"
                placeholder={I18N.t('pulseQuestion.placeholder.feedback')}
                value={answer?.comment ?? ''}
                onChangeText={comment => setAnswer({ ...answer, comment: comment })}
                onBlur={commentValidation.onInputBlurred}
                onFocus={commentValidation.onInputFocused}
                // @ts-ignore
                _error={commentValidation?.showError ? commentValidation.message : ''}
              />
            }
          />

          <Button
            debugName="Next button on Pulse questions"
            text={I18N.t('submit')}
            onPress={handleSubmit}
            disabled={submitDisabled}
            variants={['large', 'fullWidth']}
          />
        </View>
      </ScrollWrapper>

      <CategoryExplainer
        visible={categoryModal.visible}
        toggle={() => {
          categoryModal.toggle()
        }}
        category={category}
      />
    </>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    innerWrapper: {
      backgroundColor: theme.colors.neutral1,
      borderRadius: theme.borderRadius.medium,
      ...theme.presets.column,
      ...theme.presets.alignStart,
      ...theme.presets.justifyStart,
      ...theme.spacing.padding(2),
      ...theme.spacing.gap(2),
      height: 'auto',
      width: '100%'
    }
  }),
  true
)
